import {
  Checkbox,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { defaultSMTPConfigs } from "../../../tools/emails/components/defaultConfigs";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useState } from "react";

// Mailgun : l'API accepte 0 seconde entre deux envois (au lieu de 30 minimum)
// et jusqu'à 5000 envois par jour pendant le warm-up (au lieu de 1000).
const MAILGUN_DAILY_MAX = 5000;
const MAILGUN_WARM_UP = Object.fromEntries(
  Object.keys(defaultSMTPConfigs.warm_up).map((key) => [key, MAILGUN_DAILY_MAX]),
);

const EmailSending = ({ configs, mailgun = false }) => {
  const lang = useLanguage((s) => s.language);
  const mainConfig = configs || defaultSMTPConfigs;
  // Un nouvel expéditeur Mailgun démarre au maximum chaque jour ; un compte
  // existant garde sa propre courbe.
  const warmUp = configs?.warm_up || (mailgun ? MAILGUN_WARM_UP : defaultSMTPConfigs.warm_up);
  const [minValue, setMinValue] = useState(mainConfig?.sending_frequency_start);
  const [maxValue, setMaxValue] = useState(mainConfig?.sending_frequency_end);
  const [unlimited, setUnlimited] = useState(
    mainConfig?.sending_frequency_start === 0 && mainConfig?.sending_frequency_end === 0,
  );
  const noDelay = mailgun && unlimited;

  const onMinChange = (v) => {
    let value = parseInt(v);
    setMinValue(value);

    if (maxValue <= value + 30) {
      setMaxValue(value + 30);
    }
  };

  const onUnlimitedChange = (checked) => {
    setUnlimited(checked);
    if (!checked) {
      setMinValue(defaultSMTPConfigs.sending_frequency_start);
      setMaxValue(defaultSMTPConfigs.sending_frequency_end);
    }
  };

  return (
    <Stack pt={"xs"}>
      <Stack gap={"xs"}>
        <Checkbox
          size="md"
          name="enable_tracking"
          defaultChecked={mainConfig.enable_tracking}
          label={lang.emails_account.smtp.step_suppl.enable_tracking}
          styles={{ label: { fontWeight: 500 } }}
        />
        <Text size="md">
          {lang.emails_account.smtp.step_suppl.enable_tracking_text}
        </Text>
      </Stack>

      <Divider />

      <Stack gap={"xs"}>
        <Text fw={500}>
          {lang.emails_account.smtp.step_suppl.sending_frequency}
        </Text>
        {mailgun && (
          <Checkbox
            size="md"
            checked={unlimited}
            onChange={(e) => onUnlimitedChange(e.currentTarget.checked)}
            label={lang.emails_account.smtp.step_suppl.sending_frequency_unlimited}
          />
        )}
        {noDelay ? (
          // Un champ désactivé n'est pas envoyé par le formulaire : les deux
          // bornes à 0 partent par ces champs cachés.
          <>
            <input type="hidden" name="sending_frequency_start" value="0" />
            <input type="hidden" name="sending_frequency_end" value="0" />
          </>
        ) : (
          <Group>
            <Text size="md">
              {lang.emails_account.smtp.step_suppl.sending_frequency_start}
            </Text>
            <NumberInput
              defaultValue={minValue}
              name="sending_frequency_start"
              onChange={onMinChange}
              min={mailgun ? 0 : 30}
              max={3599}
              clampBehavior="blur"
              w={75}
              required
              size="md"
            />
            <Text size="md">
              {lang.emails_account.smtp.step_suppl.sending_frequency_end}
            </Text>
            <NumberInput
              defaultValue={mainConfig.sending_frequency_end}
              name="sending_frequency_end"
              min={minValue + 5}
              max={3599}
              clampBehavior="blur"
              value={maxValue}
              onChange={setMaxValue}
              required
              w={75}
              size="md"
            />
            <Text size="md">
              {lang.emails_account.smtp.step_suppl.sending_frequency_seconds}
            </Text>
          </Group>
        )}
      </Stack>

      <Divider />

      <Stack gap={4}>
        <Text fw={500}>{lang.emails_account.smtp.step_suppl.warmup}</Text>
        <Text size="md" mb={4}>
          {lang.emails_account.smtp.step_suppl.warmup_text}
        </Text>
        {mailgun && (
          <Text size="sm" c="dimmed" mb={4}>
            {lang.emails_account.smtp.step_suppl.warmup_mailgun_hint}
          </Text>
        )}
        {/* Remonté quand on passe en Mailgun sur un nouveau compte, pour
            reprendre la courbe Mailgun par défaut. */}
        <Group key={!configs && mailgun ? "mailgun" : "default"}>
          {Object.keys(warmUp).map((key, index) => (
            <NumberInput
              key={key}
              defaultValue={warmUp[`day_${index + 1}`]}
              name={`day${index + 1}`}
              label={`${lang.emails_account.smtp.step_suppl.warmup_day} ${
                index === 14 ? "15+" : index + 1
              }`}
              min={0}
              max={mailgun ? MAILGUN_DAILY_MAX : undefined}
              required
              w={100}
              mr={30}
              clampBehavior="blur"
              size="md"
            />
          ))}
        </Group>
      </Stack>
    </Stack>
  );
};

export default EmailSending;
